class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', (event) => {
        // Searching inside one facet only filters its visible values. It must
        // not refetch the product grid or replace the drawer on every keystroke.
        if (event.target?.matches?.('[data-rtcg-facet-search]')) return;
        this.debouncedOnSubmit(event);
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = () => {
      // MOB-402: URL is authoritative. The RareTCG mobile shell and other apps
      // may add history.state without a searchParams property.
      const searchParams = window.location.search.slice(1);
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.activeAbortController?.abort();
    const abortController = new AbortController();
    FacetFiltersForm.activeAbortController = abortController;
    const previousSearchParams = FacetFiltersForm.searchParamsPrev;

    document.dispatchEvent(
      new CustomEvent('rtcg:collection-updating', {
        detail: { sourceEvent: event || null, updateURLHash },
      })
    );
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    const gridContainer = document.getElementById('ProductGridContainer');
    gridContainer?.setAttribute('aria-busy', 'true');
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    gridContainer?.querySelector('.collection')?.classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    const sectionRequests = sections.map((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const cachedSection = FacetFiltersForm.filterData.find((element) => element.url === url);

      return cachedSection
        ? FacetFiltersForm.renderSectionFromCache(cachedSection, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId, abortController.signal);
    });

    return Promise.all(sectionRequests)
      .then(() => {
        if (requestId !== FacetFiltersForm.requestId) return false;
        FacetFiltersForm.searchParamsPrev = searchParams;
        if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
        FacetFiltersForm.activeAbortController = null;
        FacetFiltersForm.finishLoadingState();
        FacetFiltersForm.announceCollectionUpdate();
        return true;
      })
      .catch((error) => {
        if (requestId !== FacetFiltersForm.requestId || error?.name === 'AbortError') return false;
        FacetFiltersForm.restoreControlsFromSearchParams(previousSearchParams);
        if (!updateURLHash) FacetFiltersForm.replaceURLHash(previousSearchParams);
        FacetFiltersForm.activeAbortController = null;
        FacetFiltersForm.finishLoadingState();
        document.dispatchEvent(
          new CustomEvent('rtcg:collection-update-failed', { detail: { error, searchParams: previousSearchParams } })
        );
        console.error('RareTCG facet update failed', error);
        return false;
      });
  }

  static renderSectionFromFetch(url, event, requestId, signal) {
    return fetch(url, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Facet request failed with status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.requestId) return false;
        const html = responseText;
        // Keep section HTML cache bounded; older sessions can cycle through
        // hundreds of filter combinations and need not retain every response.
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData.filter((entry) => entry.url !== url),
          { html, url },
        ].slice(-16);
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        const animationRoot = document.getElementById('ProductGridContainer');
        if (animationRoot && typeof initializeScrollAnimationTrigger === 'function') {
          initializeScrollAnimationTrigger(animationRoot);
        }
        return true;
      });
  }

  static finishLoadingState() {
    document
      .querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner')
      .forEach((spinner) => spinner.classList.add('hidden'));
    document.getElementById('ProductGridContainer')?.querySelector('.collection')?.classList.remove('loading');
    document.getElementById('ProductGridContainer')?.removeAttribute('aria-busy');
    document.getElementById('ProductCount')?.classList.remove('loading');
    document.getElementById('ProductCountDesktop')?.classList.remove('loading');
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderSectionFromCache(cachedSection, event, requestId) {
    if (requestId !== FacetFiltersForm.requestId) return Promise.resolve(false);
    const html = cachedSection.html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    const animationRoot = document.getElementById('ProductGridContainer');
    if (animationRoot && typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(animationRoot);
    }
    return Promise.resolve(true);
  }

  static restoreControlsFromSearchParams(searchParams) {
    const params = new URLSearchParams(searchParams);
    document
      .querySelectorAll('.facets-container input[name], .facets-container select[name]')
      .forEach((control) => {
        if (control.type === 'checkbox' || control.type === 'radio') {
          control.checked = params.getAll(control.name).includes(control.value);
          return;
        }
        control.value = params.get(control.name) || '';
      });
  }

  static announceCollectionUpdate() {
    document.dispatchEvent(new CustomEvent('rtcg:collection-updated'));
  }

  static renderProductGridContainer(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductGridContainer');
    const target = document.getElementById('ProductGridContainer');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const source = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount');
    if (!source) return;
    const count = source.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const source = parsedHTML.querySelector('.facets-container');
    const target = document.querySelector('.facets-container');
    if (!source || !target) return;
    target.innerHTML = source.innerHTML;
    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static replaceURLHash(searchParams) {
    history.replaceState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sourceForm = event.target.closest('form');
    if (!sourceForm) return;

    const isMobileSource = sourceForm.id === 'FacetFiltersFormMobile' || sourceForm.id === 'FacetSortDrawerForm';
    const baseForm = isMobileSource
      ? document.getElementById('FacetFiltersFormMobile') || sourceForm
      : document.getElementById('FacetFiltersForm') || sourceForm;
    const searchParams = new URLSearchParams(new FormData(baseForm));
    [...searchParams.entries()].forEach(([key, value]) => {
      if (key.startsWith('filter.') && value === '') searchParams.delete(key);
    });

    if (sourceForm.id === 'FacetSortForm' || sourceForm.id === 'FacetSortDrawerForm') {
      searchParams.set('sort_by', event.target.value);
    }

    if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(window.location.pathname)) {
      searchParams.set('rtcg_intent', 'manual');
    }

    this.onSubmitForm(searchParams.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    const searchParams = new URLSearchParams(url);
    if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?search\/?$/i.test(window.location.pathname)) {
      searchParams.set('rtcg_intent', 'manual');
    }
    FacetFiltersForm.renderPage(searchParams.toString());
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm.requestId = 0;
FacetFiltersForm.activeAbortController = null;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
